// Pull request summaries (files, change blocks, lines and the viewer's lines
// reviewed) recorded when the viewer opens a pull request, keyed by pull
// request and head commit, so the dashboard can show them without reading
// any diff.
//
// They are kept in a runtime cache (runtime/cache.ts), one record per pull
// request, in the signed-in account's IndexedDB. The cache key names the
// server's origin, the account and SUMMARIES_VERSION, the layout of a
// summary; its inputs hash is `hashInputs()`, of nothing, because summaries
// are not worked out from anything read again later: they are recorded as
// the viewer opens each pull request, and each is already tied to its head
// commit. For the same reason they subscribe to no invalidations.
//
// The store is a runtime Program: a new account signing in resets it, which
// drops the previous account's summaries and any read or write of them
// still in flight (the previous account's storage refuses it), releases
// the previous account's lease, and reads the new account's.
import { failureOf } from '../api/failure';
import { identityUser, onIdentityChange } from '../api/identity';
import type { PrCounts } from '../logic/prSummary';
import { cacheKey, hashInputs, openCache, type Backend, type WriteResult } from '../runtime/cache';
import { after, Program, task, type Effect, type Outcome } from '../runtime/effects';
import { idbBackend } from '../runtime/idb';
import { failed, loading, manual, notAsked, ready, type Remote } from '../runtime/remote';
import { toState } from '../runtime/state.svelte';
import { prKeyOf } from './attentionInputs';

/** Summaries kept at most (the least recently recorded go first). */
export const MAX_SUMMARIES = 500;

/**
 * The layout of a stored summary (PrSummary, PrCounts); bump it when that
 * changes. 2: one record per pull request (1 kept them all in one record).
 */
export const SUMMARIES_VERSION = 2;

export interface PrSummary extends PrCounts {
  /** The head commit the counts are for. */
  head: string;
  /** When recorded (epoch ms). */
  at: number;
}

/** Where summaries persist between runs, for one account. */
export interface SummaryStore {
  /** Every summary kept, by `prKeyOf` key. */
  read(): Promise<Record<string, PrSummary>>;
  /** Keep these summaries, and delete those given as null; the rest stay as they are. */
  write(changes: Record<string, PrSummary | null>): Promise<WriteResult>;
  /** Stop using the store (another account signed in): its lease is released. */
  close(): void;
}

const isCount = (x: unknown) => typeof x === 'number' && Number.isFinite(x) && x >= 0;
const isSummary = (x: unknown): x is PrSummary => {
  if (typeof x !== 'object' || x === null) return false;
  const s = x as Record<string, unknown>;
  return typeof s.head === 'string' && isCount(s.at) && isCount(s.files) && isCount(s.blocks) && isCount(s.lines) && isCount(s.reviewed);
};

/** The well-formed summaries in a whole-map record of an earlier build. */
function legacyEntries(raw: unknown): Record<string, PrSummary> {
  const out: Record<string, PrSummary> = {};
  if (typeof raw === 'object' && raw !== null) for (const [k, v] of Object.entries(raw)) if (isSummary(v)) out[k] = v;
  return out;
}

/**
 * The summaries kept for `account` at the server at `origin`, in `backend`.
 * Reading also collects what this build no longer needs, and takes over
 * once the whole-map records earlier builds kept (the unversioned
 * `pr-summaries`, and `pr-summaries:<origin>:1`), deleting them.
 */
export function cachedSummaries(origin: string, account: string, backend: Backend = idbBackend()): SummaryStore {
  const cache = openCache<PrSummary>({
    name: 'pr-summaries',
    key: cacheKey({ origin, account, schemaVersion: SUMMARIES_VERSION, inputsHash: hashInputs() }),
    decode: (raw) => (isSummary(raw) ? raw : null),
    backend,
    limit: MAX_SUMMARIES,
  });
  const legacy = ['pr-summaries', `pr-summaries:${origin}:1`];
  // Summaries are recorded from the page's own state, not read: each is as of when it is written.
  const store = (changes: Record<string, PrSummary | null>) => cache.write(changes, cache.begin());
  let release: (() => void) | null = null;
  return {
    async read() {
      release ??= cache.hold();
      void cache.gc();
      const mine = Object.fromEntries([...(await cache.entries())].map(([k, e]) => [k, e.value]));
      const [unversioned, v1] = await Promise.all(legacy.map((k) => backend.get(k)));
      if (unversioned === undefined && v1 === undefined) return mine;
      const old = { ...legacyEntries(unversioned), ...legacyEntries((v1 as { entries?: unknown } | undefined)?.entries) };
      const taken = Object.keys(mine).length ? {} : old;
      if ((await store(taken)).ok) await backend.write(legacy.map((k) => [k, undefined] as const));
      return { ...taken, ...mine };
    },
    write: store,
    close() {
      release?.();
      release = null;
    },
  };
}

const origin = typeof location === 'undefined' ? '' : location.origin;

/** The app's summaries for `account`: none to keep without an origin (outside a browser). */
const appStore = (account: string): SummaryStore | null => (origin ? cachedSummaries(origin, account) : null);

interface State {
  store: SummaryStore | null;
  read: Remote<null>;
  entries: Record<string, PrSummary>;
  /** Changes not yet saved: null for one deleted. */
  unsaved: Record<string, PrSummary | null>;
  /** Writes in flight, and how many were started (each runs in its own lane). */
  saving: number;
  saves: number;
}

type Msg =
  | { t: 'read'; o: Outcome<Record<string, PrSummary>> }
  | { t: 'record'; key: string; summary: PrSummary }
  | { t: 'save' }
  | { t: 'saved'; batch: Record<string, PrSummary | null>; o: Outcome<WriteResult> };

const same = (a: PrSummary, b: PrSummary) =>
  a.head === b.head && a.files === b.files && a.blocks === b.blocks && a.lines === b.lines && a.reviewed === b.reviewed;

export interface PrSummariesOptions {
  /** Where `account`'s summaries are kept; null: nowhere. */
  storeFor?: (account: string) => SummaryStore | null;
  /** The account signed in now (null: nobody; undefined: not known yet). */
  account?: () => string | null | undefined;
  /** Quiet before a write, ms (the PR page records as marks change). */
  saveAfter?: number;
  /** What resets the summaries (another account signing in). */
  resetOn?: (listener: () => void) => () => void;
}

export class PrSummaries {
  private readonly program: Program<State, Msg>;
  private readonly view: { readonly current: State };

  constructor(opts: PrSummariesOptions = {}) {
    const storeFor = opts.storeFor ?? appStore;
    const account = opts.account ?? identityUser;
    const saveAfter = opts.saveAfter ?? 2000;

    const init = (): readonly [State, Effect<Msg>[]] => {
      const who = account();
      const store = who ? storeFor(who) : null;
      const s: State = { store, read: store ? loading : notAsked, entries: {}, unsaved: {}, saving: 0, saves: 0 };
      return [s, store ? [task('read', () => store.read(), (o) => ({ t: 'read', o }))] : []];
    };

    const update = (s: State, m: Msg): readonly [State, Effect<Msg>[]] => {
      switch (m.t) {
        case 'read':
          if (!m.o.ok) return [{ ...s, read: failed(failureOf(m.o.error), manual) }, []];
          // What this run recorded meanwhile wins.
          return [{ ...s, read: ready(null), entries: { ...m.o.value, ...s.entries } }, []];
        case 'record': {
          const had = s.entries[m.key];
          if (had && same(had, m.summary)) return [s, []];
          const entries = { ...s.entries, [m.key]: m.summary };
          const unsaved = { ...s.unsaved, [m.key]: m.summary };
          const keys = Object.keys(entries);
          if (keys.length > MAX_SUMMARIES)
            for (const k of keys.sort((a, b) => entries[a].at - entries[b].at || keys.indexOf(a) - keys.indexOf(b)).slice(0, keys.length - MAX_SUMMARIES)) {
              delete entries[k];
              unsaved[k] = null;
            }
          return [{ ...s, entries, unsaved }, [after('debounce', saveAfter, { t: 'save' })]];
        }
        case 'save': {
          const store = s.store;
          if (!store || !Object.keys(s.unsaved).length) return [s, []];
          const batch = s.unsaved;
          return [
            { ...s, unsaved: {}, saving: s.saving + 1, saves: s.saves + 1 },
            [task(`write ${s.saves}`, () => store.write(batch), (o) => ({ t: 'saved', batch, o }))],
          ];
        }
        case 'saved': {
          const done = { ...s, saving: s.saving - 1 };
          if (m.o.ok && m.o.value.ok) return [done, []];
          console.warn("Changeling couldn't save pull request summaries in this browser; it tries again with the next one.");
          // The next save writes them again, unless changed since.
          return [{ ...done, unsaved: { ...m.batch, ...s.unsaved } }, []];
        }
      }
    };

    const resetOn = opts.resetOn ?? onIdentityChange;
    this.program = new Program<State, Msg>({
      init,
      update,
      // The previous account's store lets go of its lease before the next one's opens.
      resetOn: (reset) =>
        resetOn(() => {
          this.program.state.store?.close();
          reset();
        }),
    });
    this.view = toState(this.program);
  }

  private get state(): State {
    return this.view.current;
  }

  /** Reading what the signed-in account saved: NotAsked while nobody is signed in. */
  get read(): Remote<null> {
    return this.state.read;
  }

  get size() {
    return Object.keys(this.state.entries).length;
  }

  /** The summary of `repo#number` at head commit `head`, or null when none was recorded at that head. */
  get(repo: string, number: number, head: string | null): PrSummary | null {
    const s = this.state.entries[prKeyOf(repo, number)];
    return s && head && s.head === head ? s : null;
  }

  /** Record `repo#number`'s counts at `head`. */
  record(repo: string, number: number, head: string, counts: PrCounts) {
    this.program.send({ t: 'record', key: prKeyOf(repo, number), summary: { head, ...counts, at: Date.now() } });
  }

  /** Save now what was recorded since the last save; resolves once no write is in flight. */
  async flush(): Promise<void> {
    this.program.send({ t: 'save' });
    await this.program.until((s) => s.saving === 0);
  }

  /** Resolves once what the signed-in account saved has been read (at once while nobody is signed in). */
  async whenRead(): Promise<void> {
    await this.program.until((s) => s.read.tag !== 'Loading');
  }
}

/** The app's summaries, for the account signed in (and nobody's until one is). */
export const prSummaries = new PrSummaries();
